import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ArrowRight, Heart } from 'lucide-react';

const EventFundraisingBar = ({ fundraising, eventId }) => {
  if (!fundraising?.enabled) return null;

  const { goal_amount = 0, current_amount = 0, title, support_text, cta_text, cta_link } = fundraising;
  const progress = goal_amount > 0 ? Math.min((current_amount / goal_amount) * 100, 100) : 0;
  const remaining = Math.max(goal_amount - current_amount, 0);

  const ctaHref = cta_link || `/donate`;
  const isExternal = ctaHref.startsWith('http');

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-xl p-6 mb-6" data-testid="event-fundraising-bar">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-4 h-4 text-[#BA1826]" fill="#BA1826" />
            <h3 className="font-heading text-base font-bold text-[#0A0A0A]" data-testid="fundraising-title">
              {title || 'Help Us Reach Our Goal'}
            </h3>
          </div>
          {support_text && (
            <p className="text-xs text-[#4B5563] leading-relaxed">{support_text}</p>
          )}
        </div>
        {isExternal ? (
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
            data-testid="fundraising-cta"
          >
            {cta_text || 'Donate Now'} <ArrowRight className="w-3 h-3" />
          </a>
        ) : (
          <Link
            to={ctaHref}
            className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
            data-testid="fundraising-cta"
          >
            {cta_text || 'Donate Now'} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
          <div
            className="h-3 rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${progress}%`,
              background: progress >= 100
                ? 'linear-gradient(90deg, #22C55E, #16A34A)'
                : 'linear-gradient(90deg, #BA1826, #DC2626)',
            }}
            data-testid="fundraising-progress"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-[#BA1826]" />
          <span className="font-bold text-[#0A0A0A]" data-testid="fundraising-raised">
            ${current_amount.toLocaleString(undefined, { minimumFractionDigits: 0 })}
          </span>
          <span className="text-[#9CA3AF]">raised</span>
        </div>
        <div className="text-[#4B5563]">
          {progress >= 100 ? (
            <span className="font-semibold text-green-600" data-testid="fundraising-complete">Goal reached!</span>
          ) : (
            <span data-testid="fundraising-remaining">
              ${remaining.toLocaleString()} to go &middot; <span className="font-semibold">{Math.round(progress)}%</span>
            </span>
          )}
        </div>
        <span className="text-[#9CA3AF]" data-testid="fundraising-goal">
          Goal: ${goal_amount.toLocaleString()}
        </span>
      </div>
    </section>
  );
};

export default EventFundraisingBar;
